import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Trash2, CheckCircle2, Ban } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStudents = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem("userEmail") || "";
  const role = localStorage.getItem("userRole");
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState("");
  const [toggling, setToggling] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    if (!email) {
      navigate("/register");
      return;
    }
    if (role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchStudents();
  }, [email, role, navigate]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/students`, {
        params: { admin_email: email },
        headers: { "x-admin-email": email },
      });
      const list = response.data || [];
      setStudents(list);
      setPage(1);
      if (list.length === 0) {
        toast.info("No assessees found");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load assessees");
    } finally {
      setLoading(false);
    }
  };

  const deleteStudent = async (studentEmail) => {
    const confirm = window.confirm(
      "Do you want to delete this assessee? This will remove all associated data including reports."
    );
    if (!confirm) return;
    setDeleting(studentEmail);
    try {
      await axios.delete(`${API}/admin/students`, {
        params: { admin_email: email, email: studentEmail },
        headers: { "x-admin-email": email },
      });
      toast.success("Assessee deleted");
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete assessee");
    } finally {
      setDeleting("");
    }
  };

  const toggleExam = async (studentEmail, nextValue) => {
    setToggling(studentEmail);
    try {
      await axios.patch(`${API}/admin/students/exam`, {
        admin_email: email,
        email: studentEmail,
        enabled: nextValue,
      }, {
        headers: { "x-admin-email": email },
      });
      setStudents((prev) =>
        prev.map((s) =>
          s.email === studentEmail ? { ...s, exam_enabled: nextValue ? 1 : 0 } : s
        )
      );
      toast.success(`Assessment ${nextValue ? "enabled" : "disabled"} for ${studentEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update assessment access");
    } finally {
      setToggling("");
    }
  };

  const filtered = students.filter((student) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (student.first_name || "").toLowerCase().includes(q) ||
      (student.last_name || "").toLowerCase().includes(q) ||
      (student.email || "").toLowerCase().includes(q)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <Navbar />
      <div className="dashboard-page">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div>
              <h1>Manage Assessees</h1>
              <p>Manage assessees</p>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                <button className="btn-secondary" onClick={() => navigate("/dashboard")}>
                  Sections
                </button>
                <button className="btn-secondary" onClick={() => navigate("/admin-results")}>
                  Assessee Reports
                </button>
                <button className="btn-secondary active-tab">
                  Assessees
                </button>
                <button className="btn-secondary" onClick={() => navigate("/admin-config")}>
                  Configuration
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                className="form-input"
                placeholder="Search by name or email"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                style={{ minWidth: "240px" }}
              />
              <button className="btn-secondary" onClick={fetchStudents}>
                Refresh
              </button>
            </div>
          </div>

          <div className="section-list">
            {loading ? (
              <div className="auth-card"><p>Loading assessees...</p></div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">
                <h3>No assessees found</h3>
                <p>Assessees will appear here after registration.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Assessment Access</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((s) => (
                      <tr key={s.email}>
                        <td>{s.first_name || "-"}</td>
                        <td>{s.last_name || "-"}</td>
                        <td>{s.email}</td>
                        <td>
                          {s.exam_completed ? (
                            <div className="exam-completed">
                              <CheckCircle2 size={16} className="exam-icon enabled" />
                              <span>Assessment Completed</span>
                            </div>
                          ) : (
                            <div className="exam-toggle">
                              {s.exam_enabled !== 0 ? (
                                <CheckCircle2 size={16} className="exam-icon enabled" />
                              ) : (
                                <Ban size={16} className="exam-icon disabled" />
                              )}
                              <label className={`switch ${toggling === s.email ? "is-loading" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={s.exam_enabled !== 0}
                                  onChange={(e) => toggleExam(s.email, e.target.checked)}
                                  disabled={toggling === s.email}
                                />
                                <span className="slider" />
                                {toggling === s.email ? <span className="switch-spinner" /> : null}
                              </label>
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn-secondary"
                            onClick={() => deleteStudent(s.email)}
                            disabled={deleting === s.email}
                          >
                            <Trash2 size={14} />
                            {deleting === s.email ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && filtered.length > pageSize && (
              <div className="table-pagination">
                <button
                  className="btn-secondary"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStudents;
